import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CustomerEngagementHub from '@/components/marketing/CustomerEngagementHub';
import BulkNumberCheck from '@/components/marketing/BulkNumberCheck';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Mail,
  MessageSquare,
  Users,
  Send,
  FileText,
  Clock,
  Filter,
  Search,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Phone,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MarketingCentre() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('engagement');
  
  // Audience Builder State
  const [audience, setAudience] = useState([]);
  const [audienceStats, setAudienceStats] = useState({ total: 0, has_mobile_count: 0, has_email_count: 0 });
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [filters, setFilters] = useState({
    service_date_from: '',
    service_date_to: '',
    next_service_from: '',
    next_service_to: '',
    booking_date_from: '',
    booking_date_to: '',
    no_show_only: false,
    no_show_from: '',
    no_show_to: '',
    has_mobile: false,
    has_email: false,
    no_visit_since_months: ''
  });
  
  // Send Message State
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendChannel, setSendChannel] = useState('sms');
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  // Campaign title — shows up as the collapsible header in the Sent Log so
  // SM can scan "Service due reminder · 25 sent" instead of decoding the
  // first 60 chars of the message preview.
  const [sendCampaignTitle, setSendCampaignTitle] = useState('');
  const [sending, setSending] = useState(false);
  
  // Templates State
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ name: '', channel: 'sms', subject: '', content: '' });
  
  // Sent Log State
  const [sentLogs, setSentLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  // Per-day collapsed/expanded state for the new accordion Sent Log view.
  // Keys: `sentlog_day_${yyyy-mm-dd}` → bool. Default-open = undefined ≠ false.
  const [expandedDays, setExpandedDays] = useState({});
  // Per-batch (campaign) state for the collapsible Sent Log. Stores both
  // the open/closed flag and the per-batch recipient list (loaded lazily
  // on first expand).
  const [expandedBatches, setExpandedBatches] = useState({});
  const [batchRecipients, setBatchRecipients] = useState({});
  const [batchLoading, setBatchLoading] = useState({});

  // Reminder Rules state — embedded scheduler UI on the Audience tab so SM
  // can set "every day at 18:30 send tomorrow's bookings template X".
  // Powered by /api/sms/rules (extended with booking_offset_days in iter115).
  const [reminderRules, setReminderRules] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [ruleFormOpen, setRuleFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleSaving, setRuleSaving] = useState(false);
  // CellCast SMS credit balance — polled every 60s while Marketing is open.
  const [smsBalance, setSmsBalance] = useState(null);
  const [newRule, setNewRule] = useState({
    name: '', template_id: '', trigger: 'daily_at',
    send_at_local: '08:00',
    source: 'booking',
    booking_offset_days: 1,
    custom_body: '',
    enabled: true,
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          
          // Owner (super_admin or shop admin) can access Marketing Centre (iter189)
          if (userData.role !== 'super_admin' && userData.role !== 'admin') {
            navigate('/');
            return;
          }
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  // Fetch templates on mount
  useEffect(() => {
    if (user) {
      fetchTemplates();
    }
  }, [user]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/marketing/templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchSentLogs = useCallback(async (page = 1) => {
    setLogsLoading(true);
    try {
      // Use the campaign-grouped endpoint so the Sent Log shows ONE
      // collapsible row per bulk-send action (date + title + counts)
      // with on-demand recipient expansion.
      const response = await fetch(`${BACKEND_URL}/api/marketing/sent-log/batches?page=${page}&page_size=30`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSentLogs(data.batches || []);
        setLogsTotalPages(data.total_pages || 1);
        setLogsPage(page);
      }
    } catch (error) {
      console.error('Failed to fetch sent logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const loadBatchRecipients = useCallback(async (batchId) => {
    if (!batchId || batchRecipients[batchId]) return;
    setBatchLoading(prev => ({ ...prev, [batchId]: true }));
    try {
      const r = await fetch(`${BACKEND_URL}/api/marketing/sent-log/batches/${encodeURIComponent(batchId)}/recipients`, {
        credentials: 'include'
      });
      if (r.ok) {
        const data = await r.json();
        setBatchRecipients(prev => ({ ...prev, [batchId]: data.recipients || [] }));
      }
    } catch (e) {
      console.error('Failed to load batch recipients', e);
    } finally {
      setBatchLoading(prev => ({ ...prev, [batchId]: false }));
    }
  }, [batchRecipients]);

  useEffect(() => {
    if (user && activeTab === 'logs') {
      fetchSentLogs(1);
    }
  }, [user, activeTab, fetchSentLogs]);

  // ── Reminder Rules ─────────────────────────────────────────────────
  const fetchReminderRules = useCallback(async () => {
    try {
      const [rulesRes, tplRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/sms/rules`, { credentials: 'include' }),
        // SMS templates live in `db.templates` with template_type=sms, not in
        // the legacy service_templates collection that /api/templates serves.
        // Use /api/template-library which queries the right collection.
        fetch(`${BACKEND_URL}/api/template-library?template_type=sms`, { credentials: 'include' }),
      ]);
      if (rulesRes.ok) {
        const d = await rulesRes.json();
        setReminderRules(d.rules || []);
      }
      if (tplRes.ok) {
        const d = await tplRes.json();
        setSmsTemplates(d.templates || []);
      }
    } catch (e) {
      console.error('Failed to fetch reminder rules', e);
    }
  }, []);

  useEffect(() => {
    if (user && activeTab === 'audience') {
      fetchReminderRules();
    }
  }, [user, activeTab, fetchReminderRules]);

  // ── CellCast SMS balance ─────────────────────────────────────────
  // Polls every 60s while the Marketing page is mounted so the SM
  // notices a low-credit situation BEFORE launching a 500-recipient
  // campaign and getting half-way through.
  const fetchSmsBalance = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/sms/balance`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setSmsBalance(d);
      }
    } catch (_e) { /* silent — show nothing on transient errors */ }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchSmsBalance();
    const id = setInterval(fetchSmsBalance, 60_000);
    return () => clearInterval(id);
  }, [user, fetchSmsBalance]);

  const openRuleForm = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setNewRule({
        name: rule.name || '',
        template_id: rule.template_id || '',
        trigger: rule.trigger || 'daily_at',
        send_at_local: rule.send_at_local || '08:00',
        source: rule.source || 'booking',
        booking_offset_days: rule.booking_offset_days ?? 1,
        custom_body: rule.custom_body || '',
        enabled: rule.enabled !== false,
      });
    } else {
      setEditingRule(null);
      setNewRule({
        name: '', template_id: '', trigger: 'daily_at',
        send_at_local: '08:00', source: 'booking',
        booking_offset_days: 1, custom_body: '', enabled: true,
      });
    }
    setRuleFormOpen(true);
  };

  const saveReminderRule = async () => {
    if (!newRule.name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    if (!newRule.template_id) {
      toast.error('Pick a message template');
      return;
    }
    const isDaily = newRule.trigger === 'daily_at';
    if (isDaily && !/^\d{2}:\d{2}$/.test(newRule.send_at_local || '')) {
      toast.error('Send time must be HH:MM (e.g. 08:30)');
      return;
    }
    setRuleSaving(true);
    try {
      const payload = {
        name: newRule.name.trim(),
        template_id: newRule.template_id,
        trigger: newRule.trigger,
        enabled: !!newRule.enabled,
        // Per-rule body override — when set, the scheduler ignores the
        // template body and sends THIS instead. Empty string clears the
        // override.
        custom_body: (newRule.custom_body || '').trim(),
      };
      if (isDaily) {
        payload.send_at_local = newRule.send_at_local;
        payload.booking_offset_days = parseInt(newRule.booking_offset_days, 10) || 0;
        payload.source = newRule.source || 'booking';
      }
      const url = editingRule
        ? `${BACKEND_URL}/api/sms/rules/${editingRule.rule_id}`
        : `${BACKEND_URL}/api/sms/rules`;
      const r = await fetch(url, {
        method: editingRule ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success(editingRule
          ? 'Rule updated'
          : (newRule.trigger === 'on_booking_created'
              ? 'Booking confirmation SMS rule created — fires immediately when SM creates a booking.'
              : 'Reminder rule created — runs daily at the time you picked.'));
        setRuleFormOpen(false);
        setEditingRule(null);
        fetchReminderRules();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save rule');
      }
    } catch (_e) {
      toast.error('Network error');
    } finally {
      setRuleSaving(false);
    }
  };

  const toggleReminderRule = async (rule) => {
    try {
      await fetch(`${BACKEND_URL}/api/sms/rules/${rule.rule_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !rule.enabled }),
      });
      fetchReminderRules();
    } catch (_e) { /* noop */ }
  };

  const deleteReminderRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? Future scheduled sends will stop.`)) return;
    try {
      await fetch(`${BACKEND_URL}/api/sms/rules/${rule.rule_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      toast.success('Rule deleted');
      fetchReminderRules();
    } catch (_e) {
      toast.error('Could not delete rule');
    }
  };

  // Human-readable label for a booking_offset_days value.
  const offsetLabel = (n) => {
    const k = parseInt(n, 10);
    if (k === 0) return 'Today';
    if (k === 1) return 'Tomorrow';
    if (k === -1) return 'Yesterday (follow-up)';
    if (k === 2) return 'Day after tomorrow';
    if (k === -180) return '6 months ago';
    if (k === -365) return '1 year ago';
    if (k === -730) return '2 years ago';
    if (k > 0) return `In ${k} days`;
    if (k <= -28) {
      const months = Math.round(Math.abs(k) / 30);
      return `~${months} month${months === 1 ? '' : 's'} ago`;
    }
    return `${Math.abs(k)} days ago`;
  };

  const buildAudience = async () => {
    setAudienceLoading(true);
    try {
      const filterPayload = {};
      if (filters.service_date_from) filterPayload.service_date_from = filters.service_date_from;
      if (filters.service_date_to) filterPayload.service_date_to = filters.service_date_to;
      if (filters.next_service_from) filterPayload.next_service_from = filters.next_service_from;
      if (filters.next_service_to) filterPayload.next_service_to = filters.next_service_to;
      if (filters.booking_date_from) filterPayload.booking_date_from = filters.booking_date_from;
      if (filters.booking_date_to) filterPayload.booking_date_to = filters.booking_date_to;
      if (filters.no_show_only) filterPayload.no_show_only = true;
      if (filters.no_show_from) filterPayload.no_show_from = filters.no_show_from;
      if (filters.no_show_to) filterPayload.no_show_to = filters.no_show_to;
      if (filters.has_mobile) filterPayload.has_mobile = true;
      if (filters.has_email) filterPayload.has_email = true;
      if (filters.no_visit_since_months) filterPayload.no_visit_since_months = parseInt(filters.no_visit_since_months);

      const response = await fetch(`${BACKEND_URL}/api/marketing/audience`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(filterPayload)
      });

      if (response.ok) {
        const data = await response.json();
        setAudience(data.customers || []);
        setAudienceStats({
          total: data.total || 0,
          has_mobile_count: data.has_mobile_count || 0,
          has_email_count: data.has_email_count || 0
        });
        setSelectedCustomers([]);
        toast.success(`Found ${data.total} customers matching criteria`);
      } else {
        toast.error('Failed to build audience');
      }
    } catch (error) {
      console.error('Failed to build audience:', error);
      toast.error('Failed to build audience');
    } finally {
      setAudienceLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedCustomers.length === audience.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(audience.map(c => c.customer_id));
    }
  };

  const toggleCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const openSendDialog = (channel) => {
    if (selectedCustomers.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    setSendChannel(channel);
    setSendSubject('');
    setSendMessage('');
    setSendCampaignTitle('');
    setSendDialogOpen(true);
  };

  const applyTemplate = (template) => {
    setSendSubject(template.subject || '');
    setSendMessage(template.content || '');
    // Pre-fill the campaign title with the template name so SM can audit
    // sends by name — they're free to edit it before sending.
    if (template.name) setSendCampaignTitle(template.name);
  };

  const sendMessages = async () => {
    if (!sendMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/marketing/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_ids: selectedCustomers,
          channel: sendChannel,
          subject: sendSubject,
          message: sendMessage,
          campaign_title: (sendCampaignTitle || '').trim() || undefined,
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.failed_count > 0) {
          // Mixed result — show warning toast with both counts so the SM
          // immediately knows some sends failed.
          toast.warning(
            `${data.sent_count} sent · ${data.failed_count} failed — check the Sent Log for reasons.`,
          );
        } else if (data.sent_count > 0) {
          toast.success(`Sent ${data.sent_count} ${sendChannel}${data.sent_count === 1 ? '' : 's'}`);
        } else {
          toast.error('Nothing was sent — selected customers had no contact details.');
        }
        setSendDialogOpen(false);
        setSelectedCustomers([]);
        // Refresh logs so the SM can drill into failures right away
        if (activeTab === 'logs') fetchSentLogs(1);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send messages');
      }
    } catch (error) {
      console.error('Failed to send messages:', error);
      toast.error('Failed to send messages');
    } finally {
      setSending(false);
    }
  };

  const saveTemplate = async () => {
    if (!newTemplate.name.trim() || !newTemplate.content.trim()) {
      toast.error('Please enter template name and content');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/marketing/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTemplate)
      });

      if (response.ok) {
        toast.success('Template saved');
        setTemplateDialogOpen(false);
        setNewTemplate({ name: '', channel: 'sms', subject: '', content: '' });
        fetchTemplates();
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/marketing/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="marketing-centre-container">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-['Chivo'] font-bold text-3xl sm:text-4xl text-slate-900 mb-2">
              Marketing Centre
            </h1>
            <p className="text-slate-500 text-base">
              Build audiences, send bulk SMS/email campaigns, and track engagement
            </p>
          </div>
          {/* CellCast credit balance card — live-polled every 60s. Turns
              amber under 100 credits + red under 25 so SM tops up before a
              campaign half-fires. */}
          {smsBalance && smsBalance.configured && (
            (() => {
              const bal = smsBalance.balance;
              const ok = smsBalance.ok && typeof bal === 'number';
              const tone =
                !ok ? 'border-rose-300 bg-rose-50 text-rose-700' :
                bal < 25 ? 'border-rose-300 bg-rose-50 text-rose-700' :
                bal < 100 ? 'border-amber-300 bg-amber-50 text-amber-800' :
                'border-emerald-300 bg-emerald-50 text-emerald-800';
              return (
                <div
                  className={`rounded-xl border-2 px-4 py-3 min-w-[180px] ${tone}`}
                  data-testid="sms-balance-card"
                  title={smsBalance.error || ''}
                >
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 flex-shrink-0" />
                    <div className="text-[11px] uppercase tracking-wide font-semibold opacity-80">
                      SMS Credits
                    </div>
                  </div>
                  {ok ? (
                    <>
                      <div className="text-2xl font-bold leading-tight mt-0.5" data-testid="sms-balance-value">
                        {Math.round(bal).toLocaleString('en-AU')}
                      </div>
                      <div className="text-[10px] opacity-70 leading-tight">
                        {bal < 25 ? 'Top up now — won\'t cover a campaign' :
                         bal < 100 ? 'Running low — top up soon' :
                         smsBalance.account_name ? smsBalance.account_name : 'Live · refreshes every 60s'}
                      </div>
                      {typeof smsBalance.dollar_balance === 'number' && (
                        <div className="text-[10px] opacity-70 mt-0.5">
                          ${smsBalance.dollar_balance.toFixed(2)} AUD balance
                          {typeof smsBalance.mms_balance === 'number' && (
                            <> · {Math.round(smsBalance.mms_balance)} MMS</>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-semibold mt-1">Couldn't fetch</div>
                      <div className="text-[10px] opacity-80 line-clamp-2">
                        {smsBalance.error || 'Check CELLCAST_API_KEY'}
                      </div>
                    </>
                  )}
                </div>
              );
            })()
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="engagement" className="data-[state=active]:bg-white" data-testid="tab-engagement">
              <Users className="h-4 w-4 mr-2" />
              Engagement Hub
            </TabsTrigger>
            <TabsTrigger value="audience" className="data-[state=active]:bg-white" data-testid="tab-audience">
              <Users className="h-4 w-4 mr-2" />
              Customer Builder
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-white" data-testid="tab-templates">
              <FileText className="h-4 w-4 mr-2" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-white" data-testid="tab-logs">
              <Clock className="h-4 w-4 mr-2" />
              Sent Log
            </TabsTrigger>
          </TabsList>

          {/* Engagement Hub Tab — clickable customer-pattern splits + bulk send */}
          <TabsContent value="engagement" className="space-y-6">
            <CustomerEngagementHub />
          </TabsContent>

          {/* Audience Builder Tab */}
          <TabsContent value="audience" className="space-y-6">
            {/* Filter Section */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="h-5 w-5 text-slate-600" />
                <h2 className="font-semibold text-lg text-slate-900">Build Your Audience</h2>
              </div>

              {/* Quick presets — most-used SM filters as one-click buttons.
                  Service-due presets pre-populate the next_service date range
                  so the SM doesn't have to type dates. Booking-based presets
                  pre-populate booking_date_from/to for SMS reminders to
                  customers booked in for today / tomorrow / this week. */}
              <div className="mb-4 flex flex-wrap gap-2" data-testid="audience-presets">
                <span className="text-xs text-slate-500 font-medium self-center mr-1">Quick filters:</span>
                {[
                  { id: 'due-7', label: 'Service due ≤ 7 days', daysAhead: 7 },
                  { id: 'due-14', label: 'Service due ≤ 14 days', daysAhead: 14 },
                  { id: 'due-30', label: 'Service due this month', daysAhead: 30 },
                  { id: 'due-90', label: 'Service due ≤ 3 months', daysAhead: 90 },
                  { id: 'overdue', label: 'Service overdue', overdue: true },
                ].map((p) => (
                  <Button
                    key={p.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid={`preset-${p.id}`}
                    onClick={() => {
                      const today = new Date();
                      const isoDay = (d) => d.toISOString().slice(0, 10);
                      if (p.overdue) {
                        // Anyone whose next_service_date is in the past — typically the
                        // highest-value win-back cohort for the SM.
                        setFilters({
                          ...filters,
                          next_service_from: '2000-01-01',
                          next_service_to: isoDay(today),
                          has_mobile: true,
                        });
                      } else {
                        const target = new Date(today.getTime() + p.daysAhead * 86400000);
                        setFilters({
                          ...filters,
                          next_service_from: isoDay(today),
                          next_service_to: isoDay(target),
                          has_mobile: true,
                        });
                      }
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
                {/* Booking-based presets — one-click cohorts of customers
                    BOOKED IN for today / tomorrow / this week. SM sends
                    them an SMS reminder right from the Compose tab. */}
                <span className="w-px h-6 bg-slate-200 mx-1 self-center" />
                {[
                  { id: 'booked-today', label: 'Booked today', offset: 0 },
                  { id: 'booked-tomorrow', label: 'Booked tomorrow', offset: 1 },
                  { id: 'booked-week', label: 'Booked this week', range: 'week' },
                ].map((p) => (
                  <Button
                    key={p.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid={`preset-${p.id}`}
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => {
                      const today = new Date();
                      const isoDay = (d) => d.toISOString().slice(0, 10);
                      let from, to;
                      if (p.range === 'week') {
                        from = isoDay(today);
                        to = isoDay(new Date(today.getTime() + 6 * 86400000));
                      } else {
                        const d = new Date(today.getTime() + p.offset * 86400000);
                        from = isoDay(d);
                        to = isoDay(d);
                      }
                      setFilters({
                        ...filters,
                        booking_date_from: from,
                        booking_date_to: to,
                        has_mobile: true,
                      });
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="preset-reset"
                  onClick={() => setFilters({
                    service_date_from: '', service_date_to: '',
                    next_service_from: '', next_service_to: '',
                    booking_date_from: '', booking_date_to: '',
                    has_mobile: false, has_email: false, no_visit_since_months: '',
                  })}
                  className="text-slate-500"
                >
                  Reset filters
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {/* Service Date Range */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Last Service Date From</Label>
                  <Input
                    type="date"
                    value={filters.service_date_from}
                    onChange={(e) => setFilters({ ...filters, service_date_from: e.target.value })}
                    data-testid="filter-service-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Last Service Date To</Label>
                  <Input
                    type="date"
                    value={filters.service_date_to}
                    onChange={(e) => setFilters({ ...filters, service_date_to: e.target.value })}
                    data-testid="filter-service-to"
                  />
                </div>
                
                {/* Next Service Date Range */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Next Service Due From</Label>
                  <Input
                    type="date"
                    value={filters.next_service_from}
                    onChange={(e) => setFilters({ ...filters, next_service_from: e.target.value })}
                    data-testid="filter-next-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Next Service Due To</Label>
                  <Input
                    type="date"
                    value={filters.next_service_to}
                    onChange={(e) => setFilters({ ...filters, next_service_to: e.target.value })}
                    data-testid="filter-next-to"
                  />
                </div>

                {/* Booking Date Range — find customers BOOKED IN for a
                    specific date or range (e.g. tomorrow's bookings, this
                    week's bookings). Powers "Booked in tomorrow" SMS
                    reminders + automated daily reminder rules below. */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                    Booked In From
                  </Label>
                  <Input
                    type="date"
                    value={filters.booking_date_from}
                    onChange={(e) => setFilters({ ...filters, booking_date_from: e.target.value })}
                    data-testid="filter-booking-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                    Booked In To
                  </Label>
                  <Input
                    type="date"
                    value={filters.booking_date_to}
                    onChange={(e) => setFilters({ ...filters, booking_date_to: e.target.value })}
                    data-testid="filter-booking-to"
                  />
                </div>
                
                {/* No Visit Since */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">No Visit Since (Months)</Label>
                  <Select
                    value={filters.no_visit_since_months || "any"}
                    onValueChange={(value) => setFilters({ ...filters, no_visit_since_months: value === "any" ? "" : value })}
                  >
                    <SelectTrigger data-testid="filter-no-visit">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="3">3 months</SelectItem>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="18">18 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* No-Show date range — narrow no_show_only to a specific
                  window (e.g. "yesterday's no-shows" or "last week's
                  no-shows"). Falls back to "all no-shows ever" when
                  both are empty. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="no-show-only"
                    checked={filters.no_show_only}
                    onCheckedChange={(checked) => setFilters({ ...filters, no_show_only: checked })}
                    data-testid="filter-no-show-only"
                  />
                  <Label htmlFor="no-show-only" className="text-sm cursor-pointer flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
                    No-Shows Only
                  </Label>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
                    No-Show From
                  </Label>
                  <Input
                    type="date"
                    value={filters.no_show_from}
                    onChange={(e) => setFilters({ ...filters, no_show_from: e.target.value })}
                    data-testid="filter-no-show-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
                    No-Show To
                  </Label>
                  <Input
                    type="date"
                    value={filters.no_show_to}
                    onChange={(e) => setFilters({ ...filters, no_show_to: e.target.value })}
                    data-testid="filter-no-show-to"
                  />
                </div>
              </div>

              {/* Contact Type Filters */}
              <div className="flex flex-wrap gap-6 mb-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has-mobile"
                    checked={filters.has_mobile}
                    onCheckedChange={(checked) => setFilters({ ...filters, has_mobile: checked })}
                    data-testid="filter-has-mobile"
                  />
                  <Label htmlFor="has-mobile" className="text-sm cursor-pointer">Has Mobile Number</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has-email"
                    checked={filters.has_email}
                    onCheckedChange={(checked) => setFilters({ ...filters, has_email: checked })}
                    data-testid="filter-has-email"
                  />
                  <Label htmlFor="has-email" className="text-sm cursor-pointer">Has Email Address</Label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={buildAudience} disabled={audienceLoading} data-testid="build-audience-btn">
                  {audienceLoading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Build Audience
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setFilters({
                    service_date_from: '',
                    service_date_to: '',
                    next_service_from: '',
                    next_service_to: '',
                    booking_date_from: '',
                    booking_date_to: '',
                    no_show_only: false,
                    no_show_from: '',
                    no_show_to: '',
                    has_mobile: false,
                    has_email: false,
                    no_visit_since_months: ''
                  })}
                >
                  Clear Filters
                </Button>
              </div>
            </Card>

            {/* ── Bulk Mobile Number Check — paste numbers, pick a period +
                category, see who visited; load non-visitors straight into
                the audience for a reminder resend. */}
            <BulkNumberCheck
              onLoadNonVisitors={(customers) => {
                const rows = customers.map(c => ({
                  customer_id: c.customer_id,
                  display_name: c.display_name,
                  email: c.email || null,
                  phone: c.phone || null,
                  last_service_date: null,
                  next_service_date: null,
                  vehicle_count: 0,
                }));
                setAudience(rows);
                setAudienceStats({
                  total: rows.length,
                  has_mobile_count: rows.filter(r => r.phone).length,
                  has_email_count: rows.filter(r => r.email).length,
                });
                setSelectedCustomers(rows.map(r => r.customer_id));
                toast.success(`${rows.length} non-visitor(s) loaded — all pre-selected, hit Send SMS below`);
              }}
            />

            {/* ── Automated Reminder Rules ─────────────────────────────
                Embedded scheduler so SM can set up daily reminder waves
                (e.g. "every day at 18:30 send tomorrow's bookings the
                appointment reminder template"). Without leaving the
                Audience tab — they already mentally live here for
                campaign work. */}
            <Card className="p-6" data-testid="reminder-rules-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-indigo-600" />
                  <h2 className="font-semibold text-lg text-slate-900">Automated Reminder Rules</h2>
                  <Badge variant="outline" className="text-xs">
                    {reminderRules.filter(r => r.enabled).length} active
                  </Badge>
                </div>
                <Button
                  size="sm"
                  onClick={() => openRuleForm(null)}
                  data-testid="new-reminder-rule-btn"
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  + New Rule
                </Button>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Schedule SMS reminders that fire automatically every day. Pick a template, a target
                cohort ("Booked today", "Booked tomorrow", etc.), and a send time. Each rule runs in
                Melbourne local time and skips customers who already received the same reminder today.
              </p>

              {/* Existing rules list */}
              {reminderRules.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="no-rules-placeholder">
                  No reminder rules yet — click <span className="font-semibold text-indigo-700">+ New Rule</span> to set one up.
                  <br />
                  Example: <em>"Every day at 18:30, send the appointment reminder to tomorrow's bookings."</em>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="reminder-rules-list">
                  {reminderRules.map((rule) => {
                    const tpl = smsTemplates.find(t => t.template_id === rule.template_id);
                    return (
                      <div
                        key={rule.rule_id}
                        className={`p-3 rounded-lg border ${rule.enabled ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200 opacity-70'}`}
                        data-testid={`reminder-rule-${rule.rule_id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900 truncate">{rule.name}</div>
                            <div className="text-xs text-slate-600 mt-0.5">
                              {rule.trigger === 'on_booking_created' ? (
                                <>Fires <strong>immediately</strong> when SM creates a booking</>
                              ) : (
                                <>
                                  <strong>
                                    {rule.source === 'no_show' ? 'No-show JCs' :
                                     rule.source === 'next_service_due' ? 'Next service due' :
                                     rule.source === 'service_date' ? 'Recent service' :
                                     'Bookings'}
                                    {' · '}
                                    {offsetLabel(rule.booking_offset_days ?? 1)}
                                  </strong>
                                  {' '}· every day at <strong>{rule.send_at_local}</strong>
                                </>
                              )}
                            </div>
                            {tpl && (
                              <div className="text-[11px] text-slate-500 mt-1 italic line-clamp-2">
                                "{(tpl.content?.body || tpl.body || '').slice(0, 80)}…"
                              </div>
                            )}
                          </div>
                          <Badge className={rule.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                            {rule.enabled ? 'Active' : 'Paused'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <Button size="sm" variant="outline" onClick={() => openRuleForm(rule)} className="h-7 text-xs" data-testid={`edit-rule-${rule.rule_id}`}>
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => toggleReminderRule(rule)} className="h-7 text-xs" data-testid={`toggle-rule-${rule.rule_id}`}>
                            {rule.enabled ? 'Pause' : 'Resume'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteReminderRule(rule)} className="h-7 text-xs text-rose-600 hover:bg-rose-50" data-testid={`delete-rule-${rule.rule_id}`}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Audience Stats */}
            {audience.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">{audienceStats.total}</div>
                    <div className="text-sm text-slate-500">Total Customers</div>
                  </div>
                </Card>
                <Card className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <Phone className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">{audienceStats.has_mobile_count}</div>
                    <div className="text-sm text-slate-500">With Mobile</div>
                  </div>
                </Card>
                <Card className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">{audienceStats.has_email_count}</div>
                    <div className="text-sm text-slate-500">With Email</div>
                  </div>
                </Card>
              </div>
            )}

            {/* Audience Table */}
            {audience.length > 0 && (
              <Card className="p-0 overflow-hidden">
                {/* Action Bar */}
                <div className="p-4 border-b bg-slate-50 flex flex-wrap items-center justify-between gap-4">
                  <div className="text-sm text-slate-600">
                    {selectedCustomers.length} of {audience.length} selected
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openSendDialog('sms')}
                      disabled={selectedCustomers.length === 0}
                      data-testid="send-sms-btn"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Send SMS
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openSendDialog('email')}
                      disabled={selectedCustomers.length === 0}
                      data-testid="send-email-btn"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Send Email
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedCustomers.length === audience.length && audience.length > 0}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Last Service</TableHead>
                        <TableHead>Next Service</TableHead>
                        <TableHead>Vehicles</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {audience.map((customer) => (
                        <TableRow key={customer.customer_id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedCustomers.includes(customer.customer_id)}
                              onCheckedChange={() => toggleCustomer(customer.customer_id)}
                              data-testid={`select-${customer.customer_id}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{customer.display_name}</TableCell>
                          <TableCell>
                            {customer.phone ? (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3 text-green-600" />
                                {customer.phone}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {customer.email ? (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3 text-purple-600" />
                                {customer.email}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {customer.last_service_date || <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell>
                            {customer.next_service_date || <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{customer.vehicle_count}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            )}

            {/* Empty State */}
            {audience.length === 0 && !audienceLoading && (
              <Card className="p-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-lg text-slate-700 mb-2">No Audience Built Yet</h3>
                <p className="text-slate-500">
                  Use the filters above to build your target audience for marketing campaigns.
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold text-lg text-slate-900">Message Templates</h2>
              <Button onClick={() => {
                setNewTemplate({ name: '', channel: 'sms', subject: '', content: '' });
                setTemplateDialogOpen(true);
              }} data-testid="add-template-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            </div>

            {templates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <Card key={template.template_id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-slate-900">{template.name}</h3>
                        <Badge variant={template.channel === 'sms' ? 'default' : 'secondary'} className="mt-1">
                          {template.channel === 'sms' ? (
                            <><MessageSquare className="h-3 w-3 mr-1" /> SMS</>
                          ) : (
                            <><Mail className="h-3 w-3 mr-1" /> Email</>
                          )}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteTemplate(template.template_id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-template-${template.template_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {template.subject && (
                      <p className="text-sm text-slate-600 mb-1"><strong>Subject:</strong> {template.subject}</p>
                    )}
                    <p className="text-sm text-slate-600 line-clamp-3">{template.content}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-lg text-slate-700 mb-2">No Templates Yet</h3>
                <p className="text-slate-500 mb-4">
                  Create reusable message templates for your marketing campaigns.
                </p>
                <Button onClick={() => setTemplateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Template
                </Button>
              </Card>
            )}
          </TabsContent>

          {/* Sent Log Tab — per-campaign collapsible cards. Each card is a
              single bulk-send action (date · title · channel · sent /
              failed counts). Expanding fetches the per-recipient list. */}
          <TabsContent value="logs" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold text-lg text-slate-900">Sent Message Log</h2>
              <Button variant="outline" onClick={() => fetchSentLogs(logsPage)} disabled={logsLoading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${logsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {sentLogs.length > 0 ? (
              <>
                {sentLogs.map((batch) => {
                  const batchId = batch.batch_id;
                  const isExpanded = !!expandedBatches[batchId];
                  const recipients = batchRecipients[batchId] || [];
                  const loading = !!batchLoading[batchId];
                  const date = new Date(batch.first_sent_at || batch.last_sent_at || new Date().toISOString());
                  const dateLabel = date.toLocaleString('en-AU', {
                    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  });
                  const title = (batch.campaign_title || batch.template_name || batch.subject || 'Untitled campaign');
                  return (
                    <Card key={batchId} className="p-0 overflow-hidden" data-testid={`sentlog-batch-${batchId}`}>
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedBatches(prev => ({ ...prev, [batchId]: !isExpanded }));
                          if (!isExpanded) loadBatchRecipients(batchId);
                        }}
                        className="w-full px-4 py-3 flex items-center gap-3 bg-slate-50 hover:bg-slate-100 border-b border-slate-200 text-left"
                        data-testid={`sentlog-batch-toggle-${batchId}`}
                      >
                        <span className={`inline-block transition-transform text-slate-500 ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={batch.channel === 'sms' ? 'default' : 'secondary'} className="uppercase">
                              {batch.channel === 'sms' ? (
                                <><MessageSquare className="h-3 w-3 mr-1" /> SMS</>
                              ) : (
                                <><Mail className="h-3 w-3 mr-1" /> Email</>
                              )}
                            </Badge>
                            <span className="font-semibold text-slate-900 truncate" data-testid={`sentlog-batch-title-${batchId}`}>{title}</span>
                          </div>
                          <div className="text-xs text-slate-600 mt-1 truncate">
                            {dateLabel}
                            {' · '}
                            <span className="font-medium">{batch.recipient_count} recipients</span>
                            {' · '}
                            <span className="text-emerald-700">{batch.delivered_count} sent</span>
                            {batch.failed_count > 0 && <span className="text-rose-700">{` · ${batch.failed_count} failed`}</span>}
                            {batch.sent_by_name && <span className="text-slate-500">{` · by ${batch.sent_by_name}`}</span>}
                          </div>
                          {batch.message_preview && (
                            <div className="text-[11px] text-slate-500 mt-1 italic line-clamp-1">
                              &ldquo;{batch.message_preview}&rdquo;
                            </div>
                          )}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="bg-white">
                          {loading ? (
                            <div className="px-4 py-4 text-sm text-slate-500 flex items-center gap-2">
                              <RefreshCw className="h-4 w-4 animate-spin" /> Loading recipients…
                            </div>
                          ) : recipients.length === 0 ? (
                            <div className="px-4 py-4 text-sm text-slate-500">No recipient details available.</div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Customer</TableHead>
                                  <TableHead>{batch.channel === 'sms' ? 'Mobile' : 'Email'}</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Sent at</TableHead>
                                  <TableHead className="hidden md:table-cell">Error</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {recipients.map((r) => (
                                  <TableRow key={r.log_id} data-testid={`sentlog-recipient-${r.log_id}`}>
                                    <TableCell className="font-medium">
                                      {r.customer_name || r.customer_id || '—'}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">{r.recipient || '—'}</TableCell>
                                    <TableCell>
                                      {(['delivered', 'sent', 'sent (mocked)'].includes(r.status)) ? (
                                        <Badge className="bg-emerald-100 text-emerald-700">
                                          <CheckCircle className="h-3 w-3 mr-1" /> Sent
                                        </Badge>
                                      ) : r.status === 'failed' ? (
                                        <Badge className="bg-rose-100 text-rose-700">
                                          <AlertCircle className="h-3 w-3 mr-1" /> Failed
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-amber-100 text-amber-700">{r.status || 'unknown'}</Badge>
                                      )}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap text-xs">
                                      {r.sent_at ? new Date(r.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                                    </TableCell>
                                    <TableCell className="hidden md:table-cell text-xs text-rose-600 max-w-xs truncate" title={r.error || ''}>
                                      {r.error || ''}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}

                {/* Pagination */}
                {logsTotalPages > 1 && (
                  <div className="p-4 flex justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchSentLogs(logsPage - 1)}
                      disabled={logsPage <= 1 || logsLoading}
                    >
                      Previous
                    </Button>
                    <span className="px-4 py-2 text-sm text-slate-600">
                      Page {logsPage} of {logsTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchSentLogs(logsPage + 1)}
                      disabled={logsPage >= logsTotalPages || logsLoading}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Card className="p-12 text-center">
                <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-lg text-slate-700 mb-2">No Messages Sent Yet</h3>
                <p className="text-slate-500">
                  Your sent message history will appear here after you send your first campaign.
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Send Message Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {sendChannel === 'sms' ? (
                <><MessageSquare className="h-5 w-5" /> Send SMS</>
              ) : (
                <><Mail className="h-5 w-5" /> Send Email</>
              )}
              <Badge variant="outline" className="ml-2">
                {selectedCustomers.length} recipients
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campaign title — surfaces as the Sent Log header */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Campaign Title
                <span className="text-[10px] uppercase tracking-wide bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">For your audit log</span>
              </Label>
              <Input
                value={sendCampaignTitle}
                onChange={(e) => setSendCampaignTitle(e.target.value)}
                placeholder="e.g. Service due reminder · April"
                data-testid="send-campaign-title-input"
              />
              <p className="text-[11px] text-slate-500">
                Used as the collapsible header in the Sent Log so you can later scan past campaigns.
              </p>
            </div>

            {/* Template Selector */}
            {templates.filter(t => t.channel === sendChannel).length > 0 && (
              <div className="space-y-2">
                <Label>Use Template</Label>
                <Select onValueChange={(templateId) => {
                  const template = templates.find(t => t.template_id === templateId);
                  if (template) applyTemplate(template);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.filter(t => t.channel === sendChannel).map((template) => (
                      <SelectItem key={template.template_id} value={template.template_id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Subject (Email only) */}
            {sendChannel === 'email' && (
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={sendSubject}
                  onChange={(e) => setSendSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  data-testid="send-subject-input"
                />
              </div>
            )}

            {/* Message */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Message *
                <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Final touch-up — edit before sending</span>
              </Label>
              <Textarea
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                placeholder={`Enter your ${sendChannel === 'sms' ? 'SMS' : 'email'} message...`}
                rows={6}
                data-testid="send-message-input"
              />
              {sendChannel === 'sms' && (
                <p className="text-xs text-slate-500">
                  Character count: {sendMessage.length} / 160 (standard SMS)
                </p>
              )}
            </div>

            {/* Note about mocked sending */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                <strong>Demo Mode:</strong> Messages will be logged but not actually sent. Connect SMS/Email providers for production use.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={sendMessages} disabled={sending || !sendMessage.trim()} data-testid="confirm-send-btn">
              {sending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send to {selectedCustomers.length} Recipients
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              <Plus className="h-5 w-5 inline mr-2" />
              New Message Template
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                placeholder="e.g., Service Reminder"
                data-testid="template-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Channel</Label>
              <Select
                value={newTemplate.channel}
                onValueChange={(value) => setNewTemplate({ ...newTemplate, channel: value })}
              >
                <SelectTrigger data-testid="template-channel-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">
                    <span className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" /> SMS
                    </span>
                  </SelectItem>
                  <SelectItem value="email">
                    <span className="flex items-center gap-2">
                      <Mail className="h-4 w-4" /> Email
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newTemplate.channel === 'email' && (
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={newTemplate.subject}
                  onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                  placeholder="Email subject line..."
                  data-testid="template-subject-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Message Content *</Label>
              <Textarea
                value={newTemplate.content}
                onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                placeholder="Enter your message template..."
                rows={5}
                data-testid="template-content-input"
              />
              <p className="text-xs text-slate-500">
                Available placeholders: {'{customer_name}'}, {'{vehicle_rego}'}, {'{invoice_total}'}, {'{invoice_id}'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveTemplate} 
              disabled={!newTemplate.name.trim() || !newTemplate.content.trim()}
              data-testid="save-template-btn"
            >
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reminder Rule form dialog ───────────────────────────────────
          Embedded creative scheduler — SM picks a target cohort relative
          to today via a friendly 5-button row, plus send time + template. */}
      <Dialog open={ruleFormOpen} onOpenChange={setRuleFormOpen}>
        <DialogContent className="max-w-lg" data-testid="reminder-rule-dialog">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Reminder Rule' : 'New Reminder Rule'}</DialogTitle>
            <DialogDescription>
              Send a daily SMS to customers based on their booking date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Rule Name</Label>
              <Input
                placeholder="e.g. Tomorrow's booking reminder"
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                data-testid="rule-name-input"
                className="mt-1"
              />
            </div>

            {/* ── Trigger picker (when does the rule fire?) ─────────── */}
            <div>
              <Label className="text-sm font-medium mb-2 block">When should this fire?</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="rule-trigger-picker">
                <button
                  type="button"
                  onClick={() => setNewRule({ ...newRule, trigger: 'daily_at' })}
                  className={`text-left p-3 rounded-lg border transition ${
                    newRule.trigger === 'daily_at'
                      ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-200'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid="trigger-daily-at"
                >
                  <div className="font-semibold text-sm text-slate-900">Every day at a set time</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">e.g. 6 PM reminder to customers booked tomorrow</div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewRule({ ...newRule, trigger: 'on_booking_created' })}
                  className={`text-left p-3 rounded-lg border transition ${
                    newRule.trigger === 'on_booking_created'
                      ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-200'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid="trigger-on-booking-created"
                >
                  <div className="font-semibold text-sm text-slate-900">When SM creates a booking</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">Confirmation SMS fires the moment a booking is saved</div>
                </button>
              </div>
            </div>

            {newRule.trigger === 'daily_at' && (
              <>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Which customers? (data source)</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="rule-source-picker">
                    {[
                      { v: 'booking', label: 'Bookings', hint: 'matches scheduled drop-off date' },
                      { v: 'next_service_due', label: 'Next service due', hint: 'matches next_service_date on invoice' },
                      { v: 'no_show', label: 'No-shows', hint: 'follow-up to missed appointments' },
                      { v: 'service_date', label: 'Recent service', hint: 'thank-you / review request' },
                    ].map(s => (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => setNewRule({ ...newRule, source: s.v })}
                        className={`text-left p-2 rounded-lg border transition ${
                          newRule.source === s.v
                            ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200'
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                        data-testid={`source-${s.v}`}
                      >
                        <div className="font-semibold text-xs text-slate-900">{s.label}</div>
                        <div className="text-[10px] text-slate-600 mt-0.5">{s.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    {newRule.source === 'no_show' ? 'Match no-show JCs marked:' :
                     newRule.source === 'next_service_due' ? 'Match customers whose next service is due:' :
                     newRule.source === 'service_date' ? 'Match customers whose last service was:' :
                     'Send to customers BOOKED IN:'}
                  </Label>
                  <div className="flex flex-wrap gap-2" data-testid="rule-offset-presets">
                    {[
                      { v: 0, label: 'Today', hint: 'morning-of' },
                      { v: 1, label: 'Tomorrow', hint: 'night-before' },
                      { v: 2, label: 'In 2 days', hint: 'extra heads-up' },
                      { v: 7, label: 'In 7 days', hint: 'weekly preview' },
                      { v: -1, label: 'Yesterday', hint: 'follow-up' },
                      { v: -180, label: '6 months ago', hint: 'half-yearly service reminder' },
                      { v: -365, label: '1 year ago', hint: 'annual win-back' },
                    ].map(p => (
                      <button
                        key={p.v}
                        type="button"
                        onClick={() => setNewRule({ ...newRule, booking_offset_days: p.v })}
                        className={`px-3 py-2 rounded-lg border text-xs font-medium transition ${
                          newRule.booking_offset_days === p.v
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                        data-testid={`offset-preset-${p.v}`}
                      >
                        <div>{p.label}</div>
                        <div className="text-[10px] opacity-70">{p.hint}</div>
                      </button>
                    ))}
                    <Input
                      type="number"
                      className="w-20 h-9 text-sm"
                      value={newRule.booking_offset_days}
                      onChange={(e) => setNewRule({ ...newRule, booking_offset_days: parseInt(e.target.value || '0', 10) })}
                      data-testid="offset-custom-input"
                      title="Custom: days from today (negative = past, positive = future)"
                    />
                  </div>
                  {(newRule.booking_offset_days <= -30 || newRule.booking_offset_days >= 30) && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      {newRule.booking_offset_days < 0
                        ? `Fires daily — sends to customers whose ${
                            newRule.source === 'service_date' ? 'last service was' :
                            newRule.source === 'next_service_due' ? 'next service was due' :
                            'event was'
                          } exactly ${Math.abs(newRule.booking_offset_days)} days ago.`
                        : `Fires daily — sends to customers whose event lands ${newRule.booking_offset_days} days from today.`}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium">Send Time (Melbourne)</Label>
                  <Input
                    type="time"
                    value={newRule.send_at_local}
                    onChange={(e) => setNewRule({ ...newRule, send_at_local: e.target.value })}
                    data-testid="rule-time-input"
                    className="mt-1"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">e.g. 18:30 = sends at 6:30 PM daily</p>
                </div>
              </>
            )}

            <div>
              <Label className="text-sm font-medium">SMS Template</Label>
              <Select
                value={newRule.template_id}
                onValueChange={(v) => setNewRule({ ...newRule, template_id: v })}
              >
                <SelectTrigger data-testid="rule-template-select" className="mt-1">
                  <SelectValue placeholder="Pick a template..." />
                </SelectTrigger>
                <SelectContent>
                  {smsTemplates.length === 0 ? (
                    <SelectItem value="__none" disabled>No SMS templates yet — create one first</SelectItem>
                  ) : smsTemplates.map(t => (
                    <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Template preview — show the body of the picked template
                  so SM knows EXACTLY what will be sent. Read-only;
                  editing is done either through the Templates tab or
                  via the per-rule "Custom message" override below. */}
              {(() => {
                const tpl = smsTemplates.find(t => t.template_id === newRule.template_id);
                if (!tpl) return null;
                const body = (tpl.content?.body || tpl.body || '');
                return (
                  <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="rule-template-preview">
                    <div className="text-[11px] uppercase font-semibold text-slate-500 tracking-wide mb-1">Template content (read-only)</div>
                    <div className="whitespace-pre-wrap text-xs text-slate-700 font-mono">
                      {body || <em className="text-slate-400">(template body is empty)</em>}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-2">
                      Placeholders: {'{customer_name}'} · {'{vehicle_rego}'} · {'{vehicle_make}'} · {'{vehicle_model}'} · {'{scheduled_date}'}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                Custom Message — Final Touch-Up
                <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Overrides template</span>
              </Label>
              <Textarea
                rows={4}
                value={newRule.custom_body}
                onChange={(e) => setNewRule({ ...newRule, custom_body: e.target.value })}
                placeholder="Leave blank to use the template above. Type here to override (e.g. add a closing-day notice). Same placeholders are supported."
                className="mt-1 text-sm font-mono"
                data-testid="rule-custom-body-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                When set, this content is sent instead of the template body. Clear the field and save to revert to the template.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={newRule.enabled}
                onCheckedChange={(c) => setNewRule({ ...newRule, enabled: !!c })}
                id="rule-enabled"
                data-testid="rule-enabled-checkbox"
              />
              <Label htmlFor="rule-enabled" className="text-sm cursor-pointer">
                Active — start sending from the next scheduled time
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleFormOpen(false)} disabled={ruleSaving}>Cancel</Button>
            <Button onClick={saveReminderRule} disabled={ruleSaving} data-testid="save-rule-btn">
              {ruleSaving ? 'Saving…' : (editingRule ? 'Save Changes' : 'Create Rule')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
